import {
  MaterialType,
  OrderResponse,
  ServiceDetail,
  ServiceLine,
  ServiceLineFile,
  ServiceToQuote,
  ServiceUnit,
} from "../../types";
import { isPricedByLine } from "../../utils/linePricing";

/**
 * Pure rules for the service editor of the launch flow (SCRUM-112, reworked for the v2 design in
 * SCRUM-117), kept out of the components so they are testable without rendering.
 */

export const MAX_SERVICE_LINES = 25;

const isService = (line: any) => line?.type === MaterialType.SERVICES;

/** A service is quoted line by line, so it has no quantity of its own. */
export const isQuotedByLine = (line: any): boolean =>
  isService(line) && (line?.serviceLines?.length ?? 0) > 0;

export const canAddServiceLine = (lines: ServiceLine[] | undefined) =>
  (lines?.length ?? 0) < MAX_SERVICE_LINES;

/** A new line: one, lump sum, nothing else filled. Never past the cap. */
export const addServiceLine = (lines: ServiceLine[] | undefined): ServiceLine[] =>
  canAddServiceLine(lines)
    ? [
        ...(lines ?? []),
        { description: "", quantity: 1, unit: ServiceUnit.LUMP_SUM, note: "", files: [] },
      ]
    : lines ?? [];

/**
 * A copy of a line right below it. The copy is a new line, so it carries no code of its own;
 * duplicates count towards the cap like any other line.
 */
export const duplicateServiceLine = (
  lines: ServiceLine[],
  index: number
): ServiceLine[] => {
  if (!canAddServiceLine(lines)) return lines;
  const { code, ...copy } = lines[index];
  return [
    ...lines.slice(0, index + 1),
    { ...copy, files: [...(copy.files ?? [])] },
    ...lines.slice(index + 1),
  ];
};

export const updateServiceLine = (
  lines: ServiceLine[],
  index: number,
  patch: Partial<ServiceLine>
): ServiceLine[] =>
  lines.map((line, i) => (i === index ? { ...line, ...patch } : line));

export const removeServiceLine = (
  lines: ServiceLine[],
  index: number
): ServiceLine[] => lines.filter((_, i) => i !== index);

export const addServiceLineFile = (
  lines: ServiceLine[],
  index: number,
  file: ServiceLineFile
): ServiceLine[] =>
  updateServiceLine(lines, index, { files: [...(lines[index].files ?? []), file] });

export const removeServiceLineFile = (
  lines: ServiceLine[],
  index: number,
  fileIndex: number
): ServiceLine[] =>
  updateServiceLine(lines, index, {
    files: (lines[index].files ?? []).filter((_, i) => i !== fileIndex),
  });

const isPositive = (value: any) => Number(value) > 0;

/** A line is complete with a description, a quantity above zero and a unit. */
export const isServiceLineComplete = (line: ServiceLine) =>
  !!line.description?.trim() && isPositive(line.quantity) && !!line.unit;

/** The objective and scope, wherever it was written (the v2 field, or a request's purpose). */
export const objectiveOf = (line: any): string =>
  line?.serviceObjective ?? line?.purpose ?? "";

/** The service's name: the quotation's own on a single-service quotation, its own otherwise. */
const titleOf = (line: any, single: boolean): string =>
  (single ? line?.name : line?.serviceTitle) ?? "";

/** What a service still lacks, in the order the step reports it (SCRUM-117). */
export type ServiceGap = "name" | "date" | "objective" | "lines" | "lineDetail";

/**
 * The first thing a service lacks, or null when it is complete. `single` says the quotation is
 * this one service: only then does the service carry the quotation's name and date of service —
 * on a joint quotation those stay in the steps where they are today.
 */
export const serviceGap = (line: any, single: boolean): ServiceGap | null => {
  if (!isService(line)) return null;
  if (!titleOf(line, single).trim()) return "name";
  if (single && !(line.startSupplyDate && line.endSupplyDate)) return "date";
  if (!objectiveOf(line).trim()) return "objective";
  const lines: ServiceLine[] = line.serviceLines ?? [];
  if (lines.length === 0) return "lines";
  if (!lines.every(isServiceLineComplete)) return "lineDetail";
  return null;
};

/** The first gap across a quotation's services, in line order. */
export const firstServiceGap = (lines: any[] | undefined): ServiceGap | null => {
  const single = (lines?.length ?? 0) === 1;
  for (const line of lines ?? []) {
    const gap = serviceGap(line, single);
    if (gap) return gap;
  }
  return null;
};

export const areServicesComplete = (lines: any[] | undefined) =>
  firstServiceGap(lines) === null;

export const hasService = (lines: any[] | undefined) =>
  (lines ?? []).some(isService);

/** What a launch sends about a service line; goods lines send nothing. */
export const serviceDetailPayload = (line: any, single = true): ServiceDetail => {
  if (!isService(line)) return {};
  return {
    serviceTitle: titleOf(line, single).trim() || undefined,
    serviceObjective: objectiveOf(line).trim() || undefined,
    serviceScope: line.serviceScope?.trim() || undefined,
    serviceDeliverables: line.serviceDeliverables?.trim() || undefined,
    // No longer asked (SCRUM-117); an older service keeps what it had.
    technicalVisit: line.technicalVisit ?? null,
    technicalVisitDate: line.technicalVisitDate || null,
    serviceRequirements: line.serviceRequirements ?? [],
    serviceLines: (line.serviceLines ?? []).map((l: ServiceLine) => ({
      ...(l.code ? { code: l.code } : {}),
      description: l.description?.trim() ?? "",
      quantity: Number(l.quantity),
      unit: l.unit,
      note: l.note?.trim() || null,
      files: (l.files ?? []).map((f) => ({ name: f.name, fileURL: f.fileURL })),
    })),
    serviceFiles: (line.serviceFiles ?? []).map((f: any) => ({
      ...(f.id ? { id: f.id } : {}),
      name: f.name,
      fileURL: f.fileURL,
      type: f.type,
    })),
  };
};

/** An existing order's service detail, back in the shape the launch form edits. */
export const serviceDetailOf = (order: OrderResponse): ServiceDetail => ({
  serviceTitle: order.serviceTitle ?? "",
  serviceObjective: order.serviceObjective ?? order.purpose ?? "",
  serviceScope: order.serviceScope ?? "",
  serviceDeliverables: order.serviceDeliverables ?? "",
  technicalVisit: order.technicalVisit ?? undefined,
  technicalVisitDate: order.technicalVisitDate ?? "",
  serviceRequirements: order.serviceRequirements ?? [],
  serviceFiles: order.serviceFiles ?? [],
  serviceLines: order.serviceLines ?? [],
});

const isServiceSource = (source: any) =>
  source?.type === MaterialType.SERVICES ||
  source?.material?.category?.type === MaterialType.SERVICES;

/**
 * SCRUM-115 (v2 in SCRUM-117): a service as the "Servicios a cotizar" card shows it, from a
 * launched order or from a line still in the launch wizard. Goods, and services launched before their detail existed,
 * give nothing.
 */
export const serviceToQuoteOf = (
  source: any,
  single = true
): ServiceToQuote | null => {
  if (!isServiceSource(source)) return null;
  const lines: ServiceLine[] = source.serviceLines ?? [];
  const hasDetail =
    !!source.serviceObjective?.trim() ||
    lines.length > 0 ||
    !!source.serviceScope?.trim() ||
    !!source.serviceDeliverables?.trim();
  if (!hasDetail) return null;
  return {
    orderId: source.orderId ?? (source.material ? source.id : undefined),
    serviceName:
      source.globalMaterialName ?? source.material?.globalMaterialName ?? source.name ?? "",
    // SCRUM-117: its own name, or the quotation's when it is the only service; none for older ones.
    title:
      source.serviceTitle?.trim() ||
      (single && !source.multipleOrder ? source.name?.trim() : "") ||
      null,
    objective: source.serviceObjective,
    scope: source.serviceScope,
    deliverables: source.serviceDeliverables,
    technicalVisit: source.technicalVisit,
    technicalVisitDate: source.technicalVisitDate,
    requirements: source.serviceRequirements ?? [],
    files: (source.serviceFiles ?? []).map((f: any) => ({
      name: f.name,
      fileURL: f.fileURL,
    })),
    lines,
  };
};

export const servicesToQuoteOf = (sources: any[] | undefined): ServiceToQuote[] =>
  (sources ?? [])
    .map((source) => serviceToQuoteOf(source, (sources ?? []).length === 1))
    .filter((s): s is ServiceToQuote => s !== null);

/**
 * SCRUM-116: the "Servicios a cotizar" card on the supplier's quoting page and on its own offer. A
 * service priced line by line shows what was asked beside its line prices, so the card leaves it out.
 */
export const servicesToQuoteAboveTheQuote = (orders: OrderResponse[] | undefined) =>
  servicesToQuoteOf(orders).filter(
    (service) => !isPricedByLine((orders ?? []).find((o) => o.id === service.orderId))
  );
